import React, { useEffect, useMemo, useState } from 'react';
import { X as CloseIcon, Shield, Search, Link2 as ShareLinkIcon, Copy, CheckCircle2 as CheckIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import { listTeams } from '../api/teams';
import { createShareGrant } from '../api/sharing';
import { VaultCrypto } from '@password-manager/crypto';
import { WebCryptoProvider } from '../crypto/WebCryptoProvider';

const EXPIRATION_PRESETS = [
  { label: '1 minute', minutes: 1 },
  { label: '5 minutes', minutes: 5 },
  { label: '30 minutes', minutes: 30 },
  { label: '1 hour', minutes: 60 },
  { label: '1 day', minutes: 60 * 24 },
  { label: '5 days', minutes: 60 * 24 * 5 },
  { label: '7 days', minutes: 60 * 24 * 7 },
  { label: '30 days', minutes: 60 * 24 * 30 },
  { label: '1 month', minutes: 60 * 24 * 30 },
  { label: '1 year', minutes: 60 * 24 * 365 },
];

const PERMISSION_OPTIONS = ['VIEW', 'COPY', 'REVEAL', 'EDIT', 'RESHARE'];

const getExpirationDate = (preset, customValue) => {
  if (preset === 'Permanent') {
    return null;
  }

  if (preset === 'Custom') {
    return customValue ? new Date(customValue).toISOString() : null;
  }

  const selected = EXPIRATION_PRESETS.find((option) => option.label === preset);
  if (!selected || selected.minutes === null) {
    return null;
  }

  return new Date(Date.now() + selected.minutes * 60 * 1000).toISOString();
};

export const ShareVaultItemModal = ({ open, item, onClose, onCreated }) => {
  const { vaultKey } = useAuth();
  const vaultCrypto = useMemo(() => new VaultCrypto(WebCryptoProvider), []);
  const [recipientType, setRecipientType] = useState('USER');
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [permissions, setPermissions] = useState(['VIEW', 'COPY']);
  const [expirationPreset, setExpirationPreset] = useState('30 minutes');
  const [customExpirationAt, setCustomExpirationAt] = useState('');
  const [maxAccessCount, setMaxAccessCount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [createdLink, setCreatedLink] = useState('');
  const [copied, setCopied] = useState(false);

  // Lock body scroll on mobile
  useEffect(() => {
    if (open) document.body.classList.add('mobile-modal-open');
    else document.body.classList.remove('mobile-modal-open');
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let active = true;
    const timeout = window.setTimeout(async () => {
      try {
        if (recipientType === 'USER') {
          if (!query.trim()) {
            setUsers([]);
            return;
          }

          const { data } = await api.get('/users/search', { params: { query } });
          if (active) {
            setUsers(data);
          }
        } else {
          const teamList = await listTeams();
          if (active) {
            const normalizedQuery = query.trim().toLowerCase();
            setTeams(
              normalizedQuery
                ? teamList.filter((team) => (team.name || '').toLowerCase().includes(normalizedQuery))
                : teamList,
            );
          }
        }
      } catch (searchError) {
        void searchError;
        if (active) {
          setError('Unable to load recipients.');
        }
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [open, recipientType, query]);

  useEffect(() => {
    if (!open) {
      setRecipientType('USER');
      setQuery('');
      setUsers([]);
      setTeams([]);
      setSelectedRecipient(null);
      setPermissions(['VIEW', 'COPY']);
      setExpirationPreset('30 minutes');
      setCustomExpirationAt('');
      setMaxAccessCount('');
      setIsSubmitting(false);
      setError('');
      setCreatedLink('');
      setCopied(false);
    }
  }, [open]);

  if (!open || !item) {
    return null;
  }

  const togglePermission = (permission) => {
    setPermissions((current) => (
      current.includes(permission)
        ? current.filter((entry) => entry !== permission)
        : [...current, permission]
    ));
  };

  const handleCreateShare = async () => {
    setIsSubmitting(true);
    setError('');
    setCreatedLink('');

    try {
      if (!vaultKey) {
        throw new Error('Vault key unavailable');
      }

      if (!selectedRecipient) {
        throw new Error('Choose a recipient');
      }

      const grantId = window.crypto.randomUUID();
      const itemKey = await vaultCrypto.extractItemKey(item.encrypted_data, vaultKey, item.id);
      const shareToken = vaultCrypto.generateItemKey();
      const wrappedItemKey = await vaultCrypto.wrapItemKey(itemKey, shareToken, grantId, 'share-grant');

      const expiresAt = getExpirationDate(expirationPreset, customExpirationAt);
      const payload = {
        id: grantId,
        vaultItemId: item.id,
        recipientUserId: recipientType === 'USER' ? selectedRecipient.id : null,
        recipientTeamId: recipientType === 'TEAM' ? selectedRecipient.id : null,
        permission: JSON.stringify(permissions),
        encryptedItemKey: {
          ...wrappedItemKey,
          shareToken,
        },
        expiresAt,
        maxAccessCount: maxAccessCount ? Number(maxAccessCount) : null,
      };

      const grant = await createShareGrant(payload);
      const nextLink = `${window.location.origin}/shares/${grant.id}?token=${encodeURIComponent(shareToken)}`;
      setCreatedLink(nextLink);
      onCreated?.(grant, nextLink);
    } catch (shareError) {
      setError(shareError?.response?.data?.message || shareError.message || 'Failed to create secure share.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!createdLink) {
      return;
    }

    await navigator.clipboard.writeText(createdLink);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const recipientResults = recipientType === 'USER' ? users : teams;

  return (
    <div
      className="share-modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        background: 'rgba(2, 6, 23, 0.75)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="share-modal-container note-right-drawer"
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '820px', background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '20px', boxShadow: '0 32px 80px rgba(0,0,0,0.45)', overflow: 'hidden' }}
      >
        <div className="share-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border-color)' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--accent-primary)' }}>
              <Shield size={18} />
              <h2 style={{ margin: 0, fontSize: '20px' }}>Share Vault Item</h2>
            </div>
            <p style={{ margin: '6px 0 0', color: 'var(--text-secondary)', fontSize: '13px' }}>The original vault item stays under your control.</p>
          </div>
          <button className="btn-icon modal-close-btn share-modal-close-btn" onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="share-modal-grid" style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '0', minHeight: '560px' }}>
          <div className="share-modal-left-col" style={{ padding: '24px', borderRight: '1px solid var(--border-color)' }}>
            <div style={{ display: 'grid', gap: '18px' }}>
              <div>
                <div className="input-label">Recipient type</div>
                <div className="share-recipient-types" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {['USER', 'TEAM'].map((type) => (
                    <button
                      key={type}
                      className={`share-type-btn ${recipientType === type ? 'active' : ''}`}
                      onClick={() => {
                        setRecipientType(type);
                        setSelectedRecipient(null);
                        setQuery('');
                      }}
                      style={{
                        padding: '10px 14px',
                        borderRadius: '12px',
                        border: `1px solid ${recipientType === type ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                        background: recipientType === type ? 'rgba(59,130,246,0.14)' : 'transparent',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      {type === 'USER' ? 'User' : 'Team'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="input-label">Search {recipientType === 'USER' ? 'user' : 'team'}</div>
                <div style={{ position: 'relative' }}>
                  <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                  <input
                    className="input-field share-search-input"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={recipientType === 'USER' ? 'Search by email' : 'Search by team name'}
                    style={{ paddingLeft: '38px', marginBottom: 0 }}
                  />
                </div>
              </div>

              <div>
                <div className="input-label">Select recipient</div>
                <div className="share-recipient-list" style={{ display: 'grid', gap: '10px', maxHeight: '190px', overflow: 'auto', paddingRight: '4px' }}>
                  {recipientResults.length === 0 ? (
                    <div style={{ color: 'var(--text-secondary)', padding: '12px 0' }}>Start typing to search recipients.</div>
                  ) : (
                    recipientResults.map((recipient) => (
                      <button
                        key={recipient.id}
                        className="share-recipient-card"
                        onClick={() => setSelectedRecipient(recipient)}
                        style={{
                          textAlign: 'left',
                          padding: '14px',
                          borderRadius: '14px',
                          border: `1px solid ${selectedRecipient?.id === recipient.id ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                          background: selectedRecipient?.id === recipient.id ? 'rgba(59,130,246,0.12)' : 'transparent',
                          color: 'var(--text-primary)',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{recipientType === 'USER' ? recipient.email : recipient.name}</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginTop: '4px' }}>
                              {recipientType === 'USER' ? recipient.role : recipient.description || 'Team'}
                            </div>
                          </div>
                          {selectedRecipient?.id === recipient.id && <CheckIcon size={18} color="var(--accent-primary)" />}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div>
                <div className="input-label">Permissions</div>
                <div className="share-permissions-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
                  {PERMISSION_OPTIONS.map((permission) => (
                    <label
                      key={permission}
                      className="share-permission-label"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 12px',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)',
                        background: permissions.includes(permission) ? 'rgba(59,130,246,0.08)' : 'transparent',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={permissions.includes(permission)}
                        onChange={() => togglePermission(permission)}
                      />
                      <span>{permission}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="share-modal-right-col" style={{ padding: '24px', display: 'grid', gap: '18px' }}>
            <div>
              <div className="input-label">Expiration</div>
              <select
                className="input-field share-select-field"
                value={expirationPreset}
                onChange={(event) => setExpirationPreset(event.target.value)}
                style={{ marginBottom: '12px' }}
              >
                {EXPIRATION_PRESETS.map((option) => (
                  <option key={option.label} value={option.label}>{option.label}</option>
                ))}
                <option value="Permanent">Permanent</option>
                <option value="Custom">Custom</option>
              </select>

              {expirationPreset === 'Custom' && (
                <input
                  type="datetime-local"
                  className="input-field share-select-field"
                  value={customExpirationAt}
                  onChange={(event) => setCustomExpirationAt(event.target.value)}
                />
              )}
            </div>

            <div>
              <div className="input-label">Maximum access count</div>
              <select className="input-field share-select-field" value={maxAccessCount} onChange={(event) => setMaxAccessCount(event.target.value)}>
                <option value="">Unlimited</option>
                <option value="1">1 access</option>
                <option value="5">5 accesses</option>
                <option value="10">10 accesses</option>
              </select>
            </div>

            <div className="share-summary-box" style={{ padding: '16px', borderRadius: '16px', border: '1px solid rgba(59,130,246,0.18)', background: 'rgba(59,130,246,0.08)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <ShareLinkIcon size={16} color="var(--accent-primary)" />
                <strong>Secure share summary</strong>
              </div>
              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: '14px' }}>
                This share stores only an encrypted item key, never plaintext secrets.
                The recipient needs the secure link and their own authenticated access.
              </div>
            </div>

            <div>
              <div className="input-label">Final confirmation</div>
              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: '14px', marginBottom: '12px' }}>
                This password will be shared securely. The original vault item will remain under your control.
              </div>
              <button
                className="share-submit-btn"
                onClick={handleCreateShare}
                disabled={isSubmitting || !selectedRecipient}
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  borderRadius: '14px',
                  border: '1px solid var(--accent-primary)',
                  background: 'var(--accent-primary)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {isSubmitting ? 'Creating secure share...' : 'Create Secure Share'}
              </button>
            </div>

            {error && <div style={{ color: 'var(--danger)' }}>{error}</div>}

            {createdLink && (
              <div style={{ padding: '16px', borderRadius: '16px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)' }}>
                <div style={{ fontWeight: 600, marginBottom: '10px' }}>Secure share link</div>
                <div style={{ wordBreak: 'break-all', color: 'var(--text-secondary)', fontSize: '14px' }}>{createdLink}</div>
                <button
                  onClick={handleCopyLink}
                  style={{
                    marginTop: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid var(--border-color)',
                    background: 'transparent',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  {copied ? <CheckIcon size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied' : 'Copy link'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
